"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaLock,
  FaKey,
  FaFileExcel,
  FaShieldAlt,
  FaUnlock,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa"
import { PaginationType, Pagination as PaginationUtils } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { useAuth } from "../../provider/auth.provider"
import axiosInstance from "../../libs/axios"
import { useDebouncedCallback } from "../../hooks/useDebounce"

interface Permission {
  id: number
  name: string
  description: string
  resource: string
}

interface RolePermission {
  permission_id: number
  permission: Permission
}

interface Role {
  id: number
  name: string
  description: string
  level: number
  reserved: boolean
  is_active?: boolean
  created_at: string
  updated_at: string
  role_permissions?: RolePermission[]
}

interface RolesListProps {
  roles: Role[]
  pagination: PaginationType
  onSearch: (search: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAddRole: () => void
  onEditRole: (role: Role) => void
  onDeleteRole: (role: Role) => void
  loading?: boolean
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  searchValue?: string
  sortState?: { field: string; direction: "ASC" | "DESC" }
}

export const RolesList: React.FC<RolesListProps> = ({
  roles,
  pagination,
  onSearch,
  onPageChange,
  onLimitChange,
  onAddRole,
  onEditRole,
  onDeleteRole,
  loading = false,
  onSort,
  searchValue = "",
  sortState,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const { hasPermission } = useAuth()
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<Record<string, any>[]>([])

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState)
    }
  }, [sortState])

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getRoleIcon = (roleName: string) => {
    const name = roleName.toLowerCase()
    if (name.includes("admin") || name.includes("super")) {
      return <FaShieldAlt className="w-4 h-4 text-red-600" />
    } else if (name.includes("manager")) {
      return <FaUsers className="w-4 h-4 text-blue-600" />
    } else if (name.includes("user") || name.includes("member")) {
      return <FaKey className="w-4 h-4 text-green-600" />
    }
    return <FaShieldAlt className="w-4 h-4 text-gray-600" />
  }

  const getPermissionCount = (role: Role): number => {
    return role.role_permissions?.length || 0
  }

  const getSearchParam = () => {
    const activeSearch = searchTerm.trim() || searchValue.trim()
    return activeSearch
  }

  const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : "")

  const mapRoleForExport = (role: Role) => {
    const permissionNames = role.role_permissions?.map((rp) => rp.permission?.name).filter(Boolean) || []

    return {
      "Role Name": role.name,
      Description: role.description || "",
      Level: role.level ?? "",
      Reserved: role.reserved ? "Yes" : "No",
      Active: role.is_active === false ? "Inactive" : "Active",
      "Permissions Count": getPermissionCount(role),
      "Permissions List": permissionNames.join(", "),
      "Created At": formatDateTime(role.created_at),
      "Updated At": formatDateTime(role.updated_at),
    }
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const params: Record<string, any> = {}

      const searchParam = getSearchParam()
      if (searchParam) params.search = searchParam

      if (sortConfig.field) {
        params.sort_by = sortConfig.field
        params.sort_order = sortConfig.direction
      }

      const limit = 100
      let page = 1
      let totalPages = 1
      const allRoles: Role[] = []

      do {
        const response = await axiosInstance.get("/roles/list", {
          params: {
            ...params,
            page,
            limit,
          },
        })

        const responseData = response.data?.data
        const pageRoles: Role[] = responseData?.roles || []
        allRoles.push(...pageRoles)

        const paginationInfo = responseData?.pagination
        if (paginationInfo) {
          totalPages =
            paginationInfo.total_pages ??
            paginationInfo.totalPages ??
            (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : totalPages)
        } else if (pageRoles.length < limit) {
          totalPages = page
        }

        page += 1
      } while (page <= totalPages)

      setExportData(allRoles.map(mapRoleForExport))
      setShowExcelModal(true)
    } catch (error) {
      console.error("Error exporting roles:", error)
    } finally {
      setExporting(false)
    }
  }

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
    setSortConfig({ field, direction })
    onSort?.(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />
    return sortConfig.direction === "ASC" ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Roles & Permissions</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? "Preparing..." : "Export Excel"}
            </button>
            {hasPermission("Role", "create") && (
              <button
                onClick={onAddRole}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add New Role
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="max-w-md">
          <label className="block text-xs font-medium text-gray-700 mb-1">Search Roles</label>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
            <input
              type="text"
              placeholder="Search by name or description"
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-1">
                  Role Name
                  {getSortIcon("name")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[250px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("description")}
              >
                <div className="flex items-center gap-1">
                  Description
                  {getSortIcon("description")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("level")}
              >
                <div className="flex items-center gap-1">
                  Level
                  {getSortIcon("level")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("reserved")}
              >
                <div className="flex items-center gap-1">
                  Reserved
                  {getSortIcon("reserved")}
                </div>
              </th>
              {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">
                Permissions Count
              </th> */}
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon("created_at")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("updated_at")}
              >
                <div className="flex items-center gap-1">
                  Updated At
                  {getSortIcon("updated_at")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading roles...</span>
                  </div>
                </td>
              </tr>
            ) : roles.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaShieldAlt className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No roles found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new role</p>
                  </div>
                </td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission("Role", "edit") && (
                        <button
                          onClick={() => onEditRole(role)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit role"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {/* {!role.reserved && hasPermission("Role", "delete") && (
                        <button
                          onClick={() => onDeleteRole(role)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                          title="Delete role"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )} */}
                      {role.reserved && (
                        <div className="text-gray-400 p-2 cursor-not-allowed" title="Reserved role cannot be deleted">
                          <FaLock className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Role Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">{getRoleIcon(role.name)}</div>
                      <span className="text-sm font-semibold text-gray-900">{role.name}</span>
                    </div>
                  </td>

                  {/* Description */}
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-900">
                      {role.description ? (
                        <span className="line-clamp-2">{role.description}</span>
                      ) : (
                        <span className="text-gray-400 italic">No description provided</span>
                      )}
                    </span>
                  </td>

                  {/* Level */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{role.level}</span>
                  </td>

                  {/* Reserved */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {role.reserved ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <FaLock className="w-2 h-2" />
                        Reserved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <FaUnlock className="w-2 h-2" />
                        Not Reserved
                      </span>
                    )}
                  </td>

                  {/* Permissions Count */}
                  {/* <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getPermissionCount(role)} permissions
                    </span>
                  </td> */}

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatDate(role.created_at)}</span>
                    </div>
                  </td>

                  {/* Updated At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formatDate(role.updated_at)}</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PaginationUtils pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="roles"
        title="Export Roles Data"
        description="Download the filtered role records with all available details"
      />
    </div>
  )
}
